/**
 * Prototype: Settings > Extensions > Basecamp > Project folders
 *
 * Renders the locked chrome against fixture data so the card can be reviewed in
 * light/dark without a Basecamp connection. Production component:
 * `components/integrations/basecamp/basecamp-project-folder-mappings-card.tsx`.
 *
 * Bet: docs/product/bets/2026-08-27-basecamp-project-folder-mappings-in-settings.md
 */

import { useMemo, useState } from 'react'
import {
  ArrowRight,
  Check,
  ChevronDown,
  FolderTree,
  Plus,
  RefreshCw,
  Search,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ExtensionSettingsShell } from '@/components/integrations/_shared/ExtensionSettingsShell'
import { cn } from '@/lib/utils'
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
} from '@/lib/menu-chrome'

const EXPAND_TRANSITION = 'grid-template-rows 520ms ease-in-out'

interface FixtureRule {
  listId: string
  listTitle: string
  folder: string
}

interface FixtureOverride {
  projectId: string
  projectName: string
  folder: string
  rules: FixtureRule[]
  lists: { id: string; title: string }[]
}

const GG_LISTS = [
  { id: '8214611243', title: 'General GGSC' },
  { id: '8214611244', title: 'GGIA' },
  { id: '8214611245', title: 'Greater Good Magazine' },
  { id: '8214611246', title: 'Mailchimp Integration' },
]

const FIXTURE_OVERRIDES: FixtureOverride[] = [
  {
    projectId: '39953224',
    projectName: 'Greater Good',
    folder: 'hop-studios/clients/greater-good',
    rules: [
      {
        listId: '8214611243',
        listTitle: 'General GGSC',
        folder: 'hop-studios/clients/greater-good/ggsc',
      },
      {
        listId: '8214611244',
        listTitle: 'GGIA',
        folder: 'hop-studios/clients/greater-good/ggia',
      },
    ],
    lists: GG_LISTS,
  },
  {
    projectId: '39179469',
    projectName: 'STAR',
    folder: 'hop-studios/clients/Star',
    rules: [],
    lists: [{ id: '8100000001', title: 'Quarterly reporting' }],
  },
]

const FIXTURE_ADDABLE = [
  { projectId: '39860713', name: 'GGIA', lists: 3 },
  { projectId: '39860152', name: 'Thnx4', lists: 2 },
  { projectId: '40010001', name: 'Bright Harbor', lists: 0 },
]

/** Fixture-driven render of the Project folders chrome, with an admin/member toggle. */
export function BasecampProjectFolderMappingsTest() {
  const [canEdit, setCanEdit] = useState(true)
  const [defaultFolder, setDefaultFolder] = useState('hop-studios/clients')
  const [overrides, setOverrides] = useState<FixtureOverride[]>(FIXTURE_OVERRIDES)
  const [removedOverrides, setRemovedOverrides] = useState<FixtureOverride[]>([])
  const [expandedProjectId, setExpandedProjectId] = useState<string | null>('39953224')
  const [addOpen, setAddOpen] = useState(false)
  const [addFilter, setAddFilter] = useState('')

  const addable = useMemo(() => {
    const needle = addFilter.trim().toLowerCase()
    return FIXTURE_ADDABLE.filter(
      (p) =>
        !overrides.some((o) => o.projectId === p.projectId) &&
        (!needle || p.name.toLowerCase().includes(needle)),
    )
  }, [addFilter, overrides])

  const example = `${defaultFolder.replace(/\/+$/, '')} / project-name / todos`.replace(
    /\//g,
    ' / ',
  )

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="flex items-center justify-between gap-3 rounded-md border border-dashed border-border px-4 py-3">
          <span className="text-xs text-muted-foreground">
            Prototype controls (not in production)
          </span>
          <label className="flex items-center gap-2 text-xs">
            Admin / owner
            <Switch checked={canEdit} onCheckedChange={setCanEdit} />
          </label>
        </div>

        <ExtensionSettingsShell
          title="Basecamp"
          description="Reach into Basecamp from this workspace. Todos stay in Basecamp; Maater links work specs and pulls on demand."
          enabled
          canToggle={false}
          statusLabel="Enabled"
        >
          <div className="space-y-5 rounded-lg border border-border bg-muted/20 px-5 py-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 space-y-2">
                <div className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                  <FolderTree className="h-4 w-4 text-muted-foreground" />
                  Project folders
                  <Badge
                    variant="outline"
                    className="text-xs font-medium text-muted-foreground"
                  >
                    {overrides.length} override{overrides.length === 1 ? '' : 's'}
                  </Badge>
                </div>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Where Basecamp work lands in Maater. Everything ingests under the default
                  folder unless a project override says otherwise. Renaming the default
                  retargets new ingest only - existing documents stay put.
                </p>
              </div>
              <Button size="sm" variant="ghost" className="shrink-0 -mr-1">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-2">
              <span className="text-xs font-medium text-muted-foreground">
                Default ingest folder
              </span>
              <Input
                value={defaultFolder}
                onChange={(e) => setDefaultFolder(e.target.value)}
                className="h-9 font-mono text-xs"
                disabled={!canEdit}
              />
              <p className="text-[11px] text-muted-foreground">
                A project with no override lands in{' '}
                <span className="font-medium text-foreground">{example}</span>
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-muted-foreground">
                  Overrides <span className="font-normal">(exceptions only)</span>
                </span>
                {canEdit ? (
                  <DropdownMenu
                    open={addOpen}
                    onOpenChange={(next) => {
                      setAddOpen(next)
                      if (!next) setAddFilter('')
                    }}
                  >
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 gap-1.5 text-xs font-normal"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Add project
                        <span className="text-muted-foreground">({addable.length})</span>
                        <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className={cn(QUIET_OVERFLOW_MENU_CONTENT_CLASS, 'w-72 p-0')}
                      collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
                      onCloseAutoFocus={(e) => e.preventDefault()}
                    >
                      <div
                        className="flex items-center gap-2 border-b border-border px-2 py-1.5"
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <input
                          value={addFilter}
                          onChange={(e) => setAddFilter(e.target.value)}
                          placeholder="Filter projects..."
                          className="h-7 min-w-0 flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground/50"
                          autoFocus
                        />
                      </div>
                      <div className="max-h-60 overflow-y-auto p-1">
                        {addable.map((p) => (
                          <DropdownMenuItem
                            key={p.projectId}
                            className="gap-2 text-xs"
                            onSelect={() => {
                              setRemovedOverrides((prev) =>
                                prev.filter((o) => o.projectId !== p.projectId),
                              )
                              setOverrides((prev) => [
                                ...prev,
                                {
                                  projectId: p.projectId,
                                  projectName: p.name,
                                  folder: `${defaultFolder.replace(/\/+$/, '')}/${p.name
                                    .toLowerCase()
                                    .replace(/[^a-z0-9]+/g, '-')}`,
                                  rules: [],
                                  lists: [],
                                },
                              ])
                              setExpandedProjectId(p.projectId)
                              setAddOpen(false)
                            }}
                          >
                            <span className="truncate">{p.name}</span>
                            <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">
                              {p.lists} lists
                            </span>
                          </DropdownMenuItem>
                        ))}
                        {addable.length === 0 ? (
                          <p className="px-2 py-3 text-xs text-muted-foreground">
                            No matching projects
                          </p>
                        ) : null}
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : null}
              </div>

              <ul className="space-y-2.5">
                {overrides.map((override) => {
                  const isExpanded = expandedProjectId === override.projectId
                  return (
                    <li
                      key={override.projectId}
                      className="rounded-md border border-border bg-background/60 px-3.5 py-3"
                    >
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm font-medium"
                          onClick={() =>
                            setExpandedProjectId(isExpanded ? null : override.projectId)
                          }
                        >
                          <ChevronDown
                            className={cn(
                              'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-500 ease-in-out',
                              isExpanded ? 'rotate-0' : '-rotate-90',
                            )}
                          />
                          <span className="truncate">{override.projectName}</span>
                          {override.rules.length > 0 ? (
                            <Badge
                              variant="outline"
                              className="shrink-0 text-[10px] font-medium text-muted-foreground"
                            >
                              {override.rules.length} list rule
                              {override.rules.length === 1 ? '' : 's'}
                            </Badge>
                          ) : null}
                        </button>
                      </div>

                      <div
                        className="grid"
                        style={{
                          gridTemplateRows: isExpanded ? '1fr' : '0fr',
                          transition: EXPAND_TRANSITION,
                        }}
                      >
                        <div className="overflow-hidden">
                          <div className="space-y-3 pt-3">
                            <div className="space-y-1.5">
                              <span className="text-[11px] font-medium text-muted-foreground">
                                Project folder
                              </span>
                              <Input
                                value={override.folder}
                                readOnly
                                className="h-8 font-mono text-xs"
                                disabled={!canEdit}
                              />
                            </div>

                            <div className="space-y-2">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-[11px] font-medium text-muted-foreground">
                                  To-do list rules
                                </span>
                                {canEdit ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 gap-1 text-[11px]"
                                    disabled={override.lists.length === 0}
                                  >
                                    <Plus className="h-3 w-3" /> Add rule
                                  </Button>
                                ) : null}
                              </div>

                              {override.lists.length === 0 ? (
                                <p className="text-[11px] text-muted-foreground">
                                  No cached to-do lists for this project yet. Open the project
                                  once in Maater, then add a rule.
                                </p>
                              ) : null}

                              {override.rules.map((rule) => (
                                <div key={rule.listId} className="flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={!canEdit}
                                    className="h-8 max-w-[180px] flex-1 justify-between gap-2 text-xs font-normal"
                                  >
                                    <span className="truncate">{rule.listTitle}</span>
                                    <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
                                  </Button>
                                  <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                  <Input
                                    value={rule.folder}
                                    readOnly
                                    className="h-8 flex-1 font-mono text-xs"
                                    disabled={!canEdit}
                                  />
                                  {canEdit ? (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 w-7 shrink-0 p-0 text-muted-foreground"
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </Button>
                                  ) : null}
                                </div>
                              ))}
                            </div>

                            {canEdit ? (
                              <div className="space-y-2 border-t border-border pt-3">
                                <p className="text-[11px] leading-relaxed text-muted-foreground">
                                  Removing this override does not delete documents. New ingest
                                  for {override.projectName} uses the default folder until you
                                  add it back.
                                </p>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      className="h-7 text-[11px] text-muted-foreground"
                                    >
                                      Remove override
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>
                                        Remove {override.projectName} override?
                                      </AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Existing documents stay put. This is a fixture - Undo
                                        restores the row.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Keep override</AlertDialogCancel>
                                      <AlertDialogAction
                                        variant="destructive"
                                        onClick={() => {
                                          setOverrides((prev) =>
                                            prev.filter((o) => o.projectId !== override.projectId),
                                          )
                                          setRemovedOverrides((prev) =>
                                            prev.some((o) => o.projectId === override.projectId)
                                              ? prev
                                              : [...prev, override],
                                          )
                                          setExpandedProjectId((prev) =>
                                            prev === override.projectId ? null : prev,
                                          )
                                        }}
                                      >
                                        Remove override
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </li>
                  )
                })}
              </ul>
              {canEdit && removedOverrides.length > 0 ? (
                <ul className="space-y-1.5">
                  {removedOverrides.map((draft) => (
                    <li
                      key={draft.projectId}
                      className="flex items-center justify-between gap-2 rounded-md border border-dashed border-border px-3 py-2"
                    >
                      <p className="min-w-0 text-[11px] text-muted-foreground">
                        Removed{' '}
                        <span className="font-medium text-foreground">{draft.projectName}</span>
                        .
                      </p>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-7 shrink-0 text-[11px]"
                        onClick={() => {
                          setRemovedOverrides((prev) =>
                            prev.filter((o) => o.projectId !== draft.projectId),
                          )
                          setOverrides((prev) =>
                            prev.some((o) => o.projectId === draft.projectId)
                              ? prev
                              : [...prev, draft],
                          )
                          setExpandedProjectId(draft.projectId)
                        }}
                      >
                        Undo
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>

            {canEdit ? (
              <div className="flex items-center justify-end gap-2">
                <Button size="sm" className="gap-1.5">
                  <Check className="h-3.5 w-3.5" /> Save
                </Button>
              </div>
            ) : (
              <p className="text-[11px] italic text-muted-foreground">
                Ask a workspace admin to change where Basecamp work lands.
              </p>
            )}
          </div>
        </ExtensionSettingsShell>
      </div>
    </div>
  )
}
